/**
 * Carrier Instructions PDF modal for SO and standalone PO sources.
 * Field order follows docgen/templates/pickup-instructions/base.hbs.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 *
 * Planned-prefill dim helpers describe ShelfCycle sources or generic not-prefilled copy
 * (`pickup-instructions/planned-prefill-hints.ts`).
 */

import { OutputTemplateDocumentType } from "@prisma/client";

import { CarrierContactPrefillField } from "~/components/domain/documents/CarrierContactPrefillField";
import { DocumentDisplayUomModalOverrideField } from "~/components/domain/settings/documents/DocumentDisplayUomModalOverrideField";
import { Form } from "~/components/Form";
import { DocumentTypedSignatureModalField } from "~/modules/orders/order-documents/DocumentTypedSignatureModalField";
import { PrintSourceRadio } from "~/modules/orders/order-documents/PrintSourceRadio";
import { CarrierRateCostPicker } from "~/modules/orders/order-documents/pickup-instructions/CarrierRateCostPicker";
import { pickupInstructionsPlannedPrefillDescription } from "~/modules/orders/order-documents/pickup-instructions/planned-prefill-hints";
import {
  type PickupInstructionsDocumentInput,
  PickupInstructionsDocumentInputSchema,
  pickupInstructionsDocumentFormInitialValues,
} from "~/modules/orders/order-documents/pickup-instructions/schema";
import { DocumentAddressOverrideEditor } from "~/modules/orders/order-menu-document-extensions/DocumentAddressOverrideEditor";
import { OptInOrderDocumentStringDateInput } from "~/modules/orders/order-menu-document-extensions/OptInOrderDocumentStringDateInput";
import { OrderDocumentSavedFacts } from "~/modules/orders/order-menu-document-extensions/OrderDocumentSavedFacts";
import type { OrderDocumentFormProps } from "~/modules/orders/order-menu-document-extensions/types";
import { useOptInOrderDocumentForm } from "~/modules/orders/order-menu-document-extensions/useOptInOrderDocumentForm";
import { ORDER_DOCUMENT_SLUG } from "~/shared/documents/order-document-settings";
import { Divider, Group, Stack, Table, TextInput } from "~/ui";
import { api } from "~/utils/api";

export const PickupInstructionsDocumentForm = ({ orderId, sourceType = "order", onSuccess, onCancel }: OrderDocumentFormProps) => {
  const displayUomQuery = api.org.getOrderDocumentDisplayUomForCreateModal.useQuery({ slug: ORDER_DOCUMENT_SLUG.pickupInstructions });
  const {
    form,
    prefillQuery,
    mutation,
    handleSubmit,
    prefillFeedback,
    isPrefillReady,
    carrierContactPrefill,
    addressCandidates,
    savedFacts,
    useOutputTemplate,
    setUseOutputTemplate,
  } = useOptInOrderDocumentForm<PickupInstructionsDocumentInput>({
    orderId,
    sourceType,
    slug: ORDER_DOCUMENT_SLUG.pickupInstructions,
    schema: PickupInstructionsDocumentInputSchema,
    onSuccess,
    initialValues: { ...pickupInstructionsDocumentFormInitialValues(), orderId, sourceType },
    hydrateOncePerSource: true,
  });

  const allowTypedSignature = displayUomQuery.data?.allowTypedSignature === true;

  return (
    <Form
      form={form}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitText="Generate PDF"
      loading={prefillQuery.isFetching || mutation.isPending || displayUomQuery.isPending}
      feedback={prefillFeedback}
      mutationError={mutation.error}
      disabled={!isPrefillReady}
    >
      <Stack spacing="sm">
        {sourceType === "order" && (
          <PrintSourceRadio
            documentType={OutputTemplateDocumentType.PICKUP_INSTRUCTIONS}
            value={useOutputTemplate}
            onChange={setUseOutputTemplate}
            legacyOptionsNote
          />
        )}
        {!useOutputTemplate && (
          <>
            <OrderDocumentSavedFacts
              facts={[
                { label: "Carrier", value: savedFacts?.carrier ?? form.values.carrier },
                { label: "Freight method", value: savedFacts?.freightMethod },
                { label: "Supplier", value: form.values.supplier },
                { label: "Has Border Crossing", value: savedFacts?.hasBorderCrossing ? "Yes" : undefined },
                { label: "Instructions", value: form.values.includeBlindPickupDelivery ? "Blind pickup / delivery" : undefined },
              ]}
            />
            <Group grow>
              <OptInOrderDocumentStringDateInput
                label="Date"
                description={pickupInstructionsPlannedPrefillDescription("documentDate")}
                value={form.values.documentDate}
                onChange={value => form.setFieldValue("documentDate", value)}
                error={form.errors.documentDate}
              />
            </Group>
            <Group grow align="flex-start">
              <CarrierContactPrefillField
                label="Carrier contact"
                value={form.values.carrierContact}
                onChange={value => form.setFieldValue("carrierContact", value)}
                error={typeof form.errors.carrierContact === "string" ? form.errors.carrierContact : undefined}
                carrierContactPrefill={carrierContactPrefill}
              />
              <TextInput
                label="Sender"
                description={pickupInstructionsPlannedPrefillDescription("senderName")}
                {...form.getInputProps("senderName")}
              />
            </Group>
            <TextInput label="Re" description={pickupInstructionsPlannedPrefillDescription("reLine")} {...form.getInputProps("reLine")} />
            <Divider label="Document addresses" />
            <DocumentAddressOverrideEditor
              withAsterisk
              label="Pick up location"
              sourceLabel="Pick up address source"
              candidates={addressCandidates}
              preferredCandidateId="pickup"
              value={form.values.addressOverrides?.pickUpLocation}
              onChange={value => form.setFieldValue("addressOverrides.pickUpLocation", value)}
              error={form.errors.pickUpLocation ?? form.errors["addressOverrides.pickUpLocation"]}
            />
            <DocumentAddressOverrideEditor
              label="Consignor"
              sourceLabel="Consignor address source"
              candidates={addressCandidates}
              preferredCandidateId="organization"
              value={form.values.addressOverrides?.consignor}
              onChange={value => form.setFieldValue("addressOverrides.consignor", value)}
              error={form.errors.consignor ?? form.errors["addressOverrides.consignor"]}
            />
            <DocumentAddressOverrideEditor
              withAsterisk
              label="Consignee"
              sourceLabel="Delivery address source"
              candidates={addressCandidates}
              preferredCandidateId="delivery"
              value={form.values.addressOverrides?.consignee}
              onChange={value => form.setFieldValue("addressOverrides.consignee", value)}
              error={form.errors.consignee ?? form.errors["addressOverrides.consignee"]}
            />
            <Divider label="Saved products and appointments" />
            <Table>
              <thead>
                <tr>
                  <th>Product / quantity</th>
                  <th>Producer / release</th>
                  <th>Pick up</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {form.values.lineItems.map(line => (
                  <tr key={line.orderItemId}>
                    <td>
                      {line.description}
                      <br />
                      {line.quantity}
                    </td>
                    <td>
                      {line.producer}
                      {line.releaseNumber ? (
                        <>
                          <br />
                          Release: {line.releaseNumber}
                        </>
                      ) : null}
                    </td>
                    <td>{[line.pickUpDate, line.pickUpAppointment].filter(Boolean).join(" · ")}</td>
                    <td>{[line.deliveryDate, line.deliveryAppointment].filter(Boolean).join(" · ")}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Divider label="Rate confirm (optional)" />
            <CarrierRateCostPicker
              candidates={prefillQuery.data?.rateCostCandidates ?? []}
              value={form.values.selectedRateCosts ?? []}
              onChange={value => form.setFieldValue("selectedRateCosts", value)}
              error={form.errors.selectedRateCosts}
            />
            {allowTypedSignature && (
              <>
                <Divider label="Signature (optional)" />
                <DocumentTypedSignatureModalField
                  value={form.values.typedSignature}
                  onChange={value => form.setFieldValue("typedSignature", value)}
                  error={typeof form.errors.typedSignature === "string" ? form.errors.typedSignature : undefined}
                />
              </>
            )}
            {displayUomQuery.data && (
              <DocumentDisplayUomModalOverrideField
                orgSettings={displayUomQuery.data}
                value={form.values.displayUomOverride}
                onChange={displayUomOverride => form.setFieldValue("displayUomOverride", displayUomOverride)}
              />
            )}
          </>
        )}
      </Stack>
    </Form>
  );
};
